import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'prettyxml',
  pure: true,
  standalone: true,
})
export class PrettyXmlPipe implements PipeTransform {
  transform(value: any, args?: any[]): string {
    const showNumbers = !!args?.[0];
    const indentSize = this.toInt(args?.[1], 2);

    const findQuery = (args?.[2] ?? '').toString();
    const activeIndex = this.toInt(args?.[3], 0);

    const raw = (value ?? '').toString();
    const trimmed = raw.trim();
    if (!trimmed) return '';

    let pretty = trimmed;
    try {
      pretty = this.formatXml(trimmed, indentSize);
    } catch {
      // If invalid XML, still show raw (escaped) rather than throwing
      pretty = trimmed;
    }

    // Escape once, then do markup highlighting on the escaped text via spans
    const escaped = this.escapeHtml(pretty);

    // Very lightweight “syntax highlighting” on escaped XML:
    // - tags
    // - attribute names
    // - attribute values
    // - comments / declarations
    let highlighted = escaped;

    // comments
    highlighted = highlighted.replace(/(&lt;!--[\s\S]*?--&gt;)/g, `<span class="xml-comment">$1</span>`);
    // declarations / doctype
    highlighted = highlighted.replace(/(&lt;\?xml[\s\S]*?\?&gt;|&lt;!DOCTYPE[\s\S]*?&gt;)/gi, `<span class="xml-decl">$1</span>`);

    // tags + attributes
    highlighted = highlighted.replace(
      /(&lt;\/?)([a-zA-Z0-9:_-]+)([\s\S]*?)(&gt;)/g,
      (_m, open, tagName, rest, close) => {
        const attrs = rest.replace(
          /(\s+)([a-zA-Z0-9:_-]+)(=)(&quot;.*?&quot;|&#39;.*?&#39;)/g,
          (_m2, ws, attrName, eq, attrVal) =>
            `${ws}<span class="xml-attr">${attrName}</span>${eq}<span class="xml-attrval">${attrVal}</span>`
        );

        return `${open}<span class="xml-tag">${tagName}</span>${attrs}${close}`;
      }
    );

    // Apply find highlighting after token highlighting, only in text parts (not inside tags)
    highlighted = this.applyFindHighlight(highlighted, findQuery, activeIndex);

    if (!showNumbers) return highlighted;

    const lines = highlighted.split('\n');
    const width = String(lines.length).length;
    const nums = lines.map((_, i) => String(i + 1).padStart(width, ' ')).join('\n');

    return `
<div class="pjv-lnwrap">
  <pre class="pjv-ln" aria-hidden="true">${nums}</pre>
  <pre class="pjv-code">${lines.join('\n')}</pre>
</div>`.trim();
  }

  private formatXml(xml: string, indentSize: number): string {
    const input = (xml ?? '').toString().trim();
    if (!input) return input;

    const doc = new DOMParser().parseFromString(input, 'application/xml');
    const parseError = doc.getElementsByTagName('parsererror')?.[0];
    if (parseError) throw new Error((parseError.textContent ?? 'Invalid XML').trim());

    // normalize tags to lines
    const reg = /(>)(<)(\/*)/g;
    const normalized = input.replace(reg, '$1\n$2$3');
    const lines = normalized.split('\n').map(l => l.trim()).filter(Boolean);

    let indent = 0;
    const pad = (n: number) => ' '.repeat(Math.max(0, n) * indentSize);
    const out: string[] = [];

    for (const line of lines) {
      const isClosing = /^<\/.+>/.test(line);
      const isOpening = /^<[^!?/][^>]*[^/]>$/.test(line);
      const isSelfClosing = /^<[^>]+\/>$/.test(line);
      const isDeclOrComment = /^<\?xml|^<!--|^<!DOCTYPE/i.test(line);

      if (isClosing) indent -= 1;

      out.push(pad(indent) + line);

      if (isDeclOrComment) continue;
      if (isOpening && !isSelfClosing) indent += 1;
    }

    return out.join('\n').replace(/\n{3,}/g, '\n\n');
  }

  private applyFindHighlight(html: string, query: string, activeIndex: number): string {
    const q = (query ?? '').toString().trim();
    if (!q) return html;

    const escapedNeedle = this.escapeHtml(q);
    if (!escapedNeedle.trim()) return html;

    const rx = new RegExp(this.escapeRegExp(escapedNeedle), 'gi');

    let matchIndex = 0;
    const parts = html.split(/(<[^>]+>)/g);

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part || part.startsWith('<')) continue;

      parts[i] = part.replace(rx, (m) => {
        const isActive = matchIndex === activeIndex;
        const cls = isActive ? 'it-mark is-active' : 'it-mark';
        const out = `<mark class="${cls}" id="it-mi-${matchIndex}" data-mi="${matchIndex}">${m}</mark>`;
        matchIndex++;
        return out;
      });
    }

    return parts.join('');
  }

  private escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private escapeHtml(s: string): string {
    return (s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private toInt(v: any, fallback: number): number {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : fallback;
  }
}
